/*
  Product form (snippets/buy-buttons.liquid): `add;` through the Ajax Cart API.

  The form's submit buttons are the main button and the sticky one (both in form.elements, the
  sticky one through its `form` attribute). Until a size is picked they read `pick a size;`, and
  pressing one brings the sizes into view, focuses the first one in stock, shakes the row (not under
  reduced motion; once the scroll has settled, so it shows) and says "pick a size first" through the
  page's announcer. After a pick they read `add; M` (`add;` for a product with a single variant).
  While adding, the pressed button shows the semicolon loader. The cart drawer (assets/cart-drawer.js) then
  re-renders the receipt and `cart;N` from the sections in the response and opens, as Dawn's product
  form did; focus returns to the pressed button when it closes. On phones the added toast shows instead
  (snippets/cart-toast.liquid; the gallery flies only from the buy box's `add;`) and focus stays put.
  With the cart type set to page there is no drawer, and the add goes on to /cart. A failed add says
  why by the pressed button, at every width (so no error toast): in the buy box, or in the sticky bar's
  own message when its button was pressed (the buy box is then above the screen).

  Without JavaScript the form posts to /cart/add, and the `required` size radios make the browser
  ask for a size first. With JavaScript the radios lose `required`, so they don't read as invalid
  before anyone has picked (C16); the buttons ask for a size instead.
*/
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.errorMessage = this.querySelector('.product-form__error');
        this.form.noValidate = true;
        this.sizes.forEach((size) => size.removeAttribute('required'));
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        // The no-JavaScript `add;` label; from here on update() relabels the buttons.
        this.querySelector('.product-form__label-picked')?.remove();
        this.update();
      }

      get buttons() {
        return [...this.form.elements].filter((element) => element.type === 'submit');
      }

      get sizes() {
        return [...this.form.elements].filter((element) => element.name === 'id' && element.type === 'radio');
      }

      get variantId() {
        return new FormData(this.form).get('id');
      }

      // `add; M` once a size is picked; `add;` when there is only one variant (nothing to choose).
      get readyLabel() {
        const sizes = this.sizes;
        const size = sizes.find((element) => element.checked)?.dataset.size;
        if (sizes.length < 2 || !size) return this.dataset.labelAdd;
        return this.dataset.labelAddSize.replace('[size]', () => size);
      }

      // Called by assets/product-info.js when a size is picked.
      update() {
        const ready = Boolean(this.variantId);
        const label = ready ? this.readyLabel : this.dataset.labelPick;
        this.buttons.forEach((button) => {
          if (button.disabled) return;
          this.setDisabled(button, !ready);
          button.querySelector('.product-form__label').textContent = label;
        });
        this.showError('');
      }

      // No size yet: bring the sizes into view, focus the first one that's in stock, shake the row and
      // say why. Focus lands on the sizes for keyboard and screen reader users; the announcement says
      // what the shake shows.
      pickSize() {
        const size = this.sizes.find((element) => !element.disabled);
        if (!size) return;
        const row = size.closest('.size-picker__options') || size.parentElement;
        const inView = this.isInView(row);
        if (!inView) size.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
        size.focus({ preventScroll: true });
        this.shake(row, inView);
        announce(this.dataset.labelPickFirst);
      }

      // Fully visible between the sticky header and the phone's sticky bar (the page's scroll-padding).
      isInView(element) {
        const style = getComputedStyle(document.documentElement);
        const top = parseFloat(style.scrollPaddingTop) || 0;
        const bottom = parseFloat(style.scrollPaddingBottom) || 0;
        const rect = element.getBoundingClientRect();
        return rect.top >= top && rect.bottom <= window.innerHeight - bottom;
      }

      // The design's shake (translateX 0, -9, 8, 0 over MOTION.shake). When the sizes were off screen
      // (the sticky bar's button), it waits for the scroll to end, so it shows. None under reduced motion.
      shake(row, settled) {
        if (!row.animate || reducedMotion()) return;
        let done = false;
        const run = () => {
          if (done || reducedMotion()) return;
          done = true;
          row.animate(
            [
              { transform: 'translateX(0)' },
              { transform: 'translateX(-9px)' },
              { transform: 'translateX(8px)' },
              { transform: 'translateX(0)' },
            ],
            { duration: MOTION.shake }
          );
        };
        if (settled) {
          run();
          return;
        }
        if ('onscrollend' in window) document.addEventListener('scrollend', run, { once: true });
        setTimeout(run, 'onscrollend' in window ? 1000 : 400);
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.busy) return;

        const variantId = this.variantId;
        if (!variantId) {
          this.pickSize();
          return;
        }

        const button = event.submitter?.type === 'submit' ? event.submitter : this.buttons[0];
        this.showError('');
        this.setLoading(button, true);

        const cart = document.querySelector('cart-drawer');
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        const linesUpdate = this.startCartLinesUpdate(variantId);

        fetch(window.routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Shopify's own sentence (English, capitalised) goes only to the events. The visitor
              // gets a locale string: a 422 is a refusal (the cart already holds all the stock, or
              // the size sold out since the page loaded); anything else is the general error.
              const message = response.description || response.message || window.cartStrings.error;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.dispatchCartErrorEvent(message, 'INVALID');
              linesUpdate?.reject(new Error(message));
              this.showError(
                response.status === 422 ? window.cartStrings.addRefused : window.cartStrings.error,
                button
              );
              return;
            }

            this.resolveCartLinesUpdate(linesUpdate);
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            });
            // Set only once the add worked: after a failed one the drawer doesn't keep the button.
            cart.setActiveElement(button);
            // The phone toast: the gallery flies for the buy box's button only; one size leaves the size out.
            const product = button.closest('[data-sticky-add]') ? null : this.closest('product-component');
            cart.renderContents(response, {
              from: product?.querySelector('media-gallery, [data-header-stage]') || null,
              oneSize: this.sizes.length < 2,
            });
          })
          .catch((error) => {
            console.error(error);
            this.dispatchCartErrorEvent(error.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdate?.reject(error);
            this.showError(window.cartStrings.error, button);
          })
          .finally(() => this.setLoading(button, false));
      }

      setLoading(button, isLoading) {
        this.busy = isLoading;
        button.classList.toggle('loading', isLoading);
        this.setDisabled(button, isLoading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      // aria-disabled, not disabled: the button stays focusable and keeps its label for screen readers.
      setDisabled(button, isDisabled) {
        if (isDisabled) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      }

      // The message goes by `button`: the sticky bar's slot (sections/main-product.liquid) while the
      // bar shows and its button was pressed, else the buy box's. The other slot is emptied, so the
      // message is announced once; an empty message empties both.
      showError(message, button) {
        const bar = this.buttons.map((element) => element.closest('[data-sticky-add]')).find(Boolean);
        const sticky = bar?.querySelector('.product-sticky__error');
        const shown = bar?.classList.contains('is-shown');
        const target = sticky && shown && bar.contains(button) ? sticky : this.errorMessage;
        [this.errorMessage, sticky].forEach((slot) => {
          if (!slot) return;
          slot.textContent = slot === target ? message : '';
          slot.hidden = !slot.textContent;
        });
      }

      // Shopify standard events, as Dawn's product form sends them for an add to cart.
      startCartLinesUpdate(variantId) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        const event = new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferred.promise,
        });
        // The event wraps the promise in one of its own, which a failed add rejects. Handled here, so
        // the failure shows only as the form's message, not as an uncaught error; listeners still get
        // the rejection.
        (event.promise || deferred.promise).catch(() => {});
        this.dispatchEvent(event);
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!deferred || !CartLinesUpdateEvent) return;

        const cartData =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${window.routes.cart_url}.json`).then((response) => response.json());

        cartData
          .then((cart) => {
            if (!cart?.currency) throw new Error('Missing currency in cart response');
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((error) => deferred.reject(error));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }
    }
  );
}
